import { createTheme } from '@mui/material';

import { alertOverrides } from './overrides/alertOverrides';
import { autocompleteOverrides } from './overrides/autocompleteOverrides';
import { buttonOverrides } from './overrides/buttonOverrides';
import { chipOverrides } from './overrides/chipOverrides';
import {
  dialogActionsOverride,
  dialogContentOverride,
  dialogOverride,
  dialogTitleOverride,
} from './overrides/dialogOverrides';
import { dividerOverrides } from './overrides/dividerOverrides';
import { formHelperTextOverrides } from './overrides/formHelperTextOverrides';
import { iconButtonOverrides } from './overrides/iconButtonOverrides';
import { baseInputOverride, inputLabelOverride, outlinedInputOverride } from './overrides/inputOverrides';
import { linearProgressOverrides } from './overrides/linearProgressOverrides';
import { linkOverrides } from './overrides/linkOverrides';
import { modalOverrides } from './overrides/modalOverrides';
import { switchOverrides } from './overrides/switchOverrides';

import scssVariables from 'styles/sass-variables.module.scss';

const { palette, breakpoints } = createTheme();
export const theme = createTheme({
  breakpoints: {
    values: {
      ...breakpoints.values,
      sm: 576,
    },
  },
  palette: {
    primary: {
      main: scssVariables.primary_purple_default,
    },
    error: {
      main: scssVariables.red_default,
      light: scssVariables.red_200,
    },
    success: {
      main: scssVariables.green_default,
    },
    warning: {
      main: scssVariables.orange_default,
    },
    info: {
      main: scssVariables.blue_default,
    },
    gradient: palette.augmentColor({
      color: {
        main: scssVariables.primary_purple_1000,
      },
    }),
  },
  typography: {
    fontFamily: ['Plus Jakarta Sans', 'Manrope', 'Roboto', 'sans-serif'].join(','),

    allVariants: {
      color: scssVariables.text_primary,
    },

    h1: {
      fontSize: 40,
      fontWeight: 700,
      lineHeight: '40px',
    },
    h2: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: '32px',
    },
    h3: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: '28px',
    },
    h4: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: '24px',
    },
    h5: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '24px',
    },
    h6: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '22px',
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '24px',
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '22px',
    },
    subtitle1: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20px',
      color: scssVariables.greys_text,
    },
    subtitle2: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '18px',
    },

    button: {
      lineHeight: '16px',
      textTransform: 'none',
    },
  },
  mixins: {
    toolbar: {
      height: scssVariables.headerHeight,

      [breakpoints.down('sm')]: {
        height: scssVariables.headerHeightMobile,
      },
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        fullWidth: false,
        disableRipple: true,
      },
      styleOverrides: buttonOverrides,
    },
    MuiIconButton: {
      styleOverrides: iconButtonOverrides,
    },
    MuiInputLabel: {
      styleOverrides: inputLabelOverride,
    },
    MuiFormHelperText: {
      styleOverrides: formHelperTextOverrides,
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiInputBase: {
      styleOverrides: baseInputOverride,
    },

    MuiOutlinedInput: {
      styleOverrides: outlinedInputOverride,
    },
    MuiAutocomplete: {
      styleOverrides: autocompleteOverrides,
    },
    MuiModal: {
      styleOverrides: modalOverrides,
    },
    MuiLink: {
      styleOverrides: linkOverrides,
    },
    MuiSwitch: {
      styleOverrides: switchOverrides,
    },
    MuiDivider: {
      styleOverrides: dividerOverrides,
    },
    MuiDialog: {
      styleOverrides: dialogOverride,
    },
    MuiDialogTitle: {
      styleOverrides: dialogTitleOverride,
    },
    MuiDialogContent: {
      styleOverrides: dialogContentOverride,
    },
    MuiDialogActions: {
      styleOverrides: dialogActionsOverride,
    },
    MuiLinearProgress: {
      styleOverrides: linearProgressOverrides,
    },
    MuiAlert: {
      styleOverrides: alertOverrides,
    },
    MuiChip: {
      styleOverrides: chipOverrides,
    },
  },
});

export type TTheme = typeof theme;
